/**
 * CampagnesListPage — /soutien/campagnes (Sprint 4)
 *
 * Liste publique des campagnes AITO x Athlète actives.
 * Pour chaque campagne : miniature, athlète, produit, compteur X/20 confirmées, CTA voir.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Shirt, ArrowRight, Trophy, ShieldCheck, CheckCircle2 } from "lucide-react";
import { resolveAthletePhoto } from "@/lib/photoFallback";

export default function CampagnesListPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/campaigns").then((r) => setItems(r.data.items || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6" data-testid="campagnes-list-page">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-display font-bold flex items-center gap-2">
          <Shirt className="h-6 w-6 text-[hsl(var(--aito-gold))]" />Campagnes AITO x Athlète
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Séries limitées en précommande. Chaque tranche est validée lorsqu'elle atteint 20 précommandes confirmées.
        </p>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-64" />)}</div>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          Aucune campagne active pour le moment. Revenez bientôt — les athlètes proposent régulièrement de nouveaux projets.
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((c) => <CampaignCard key={c.id} c={c} />)}
        </div>
      )}
    </div>
  );
}

function CampaignCard({ c }) {
  const currentTranche = (c.tranches || []).find((t) => t.tranche_number === c.current_tranche_number) || (c.tranches || []).slice(-1)[0];
  const confirmed = currentTranche?.confirmed_count || 0;
  const batch = currentTranche?.batch_size || c.batch_size || 20;
  const pct = Math.min(100, Math.round((confirmed / batch) * 100));
  const isThresholdReached = c.status === "threshold_reached" || confirmed >= batch;
  return (
    <Card className="overflow-hidden hover:shadow-md transition" data-testid={`campagne-card-${c.slug}`}>
      {/* V12.x — Miniature = photo de la fiche athlète en priorité (identité) puis image de campagne, puis fallback icône. */}
      {c.athlete ? (
        <div className="aspect-video bg-white flex items-center justify-center overflow-hidden">
          <img src={resolveAthletePhoto(c.athlete)} alt={c.athlete.full_name}
            className={`h-full w-full ${c.athlete.photo_url ? "object-cover object-top" : "object-contain p-4"}`}
            data-testid={`campagne-card-athlete-photo-${c.slug}`} />
        </div>
      ) : c.image_url ? (
        <div className="aspect-video bg-secondary" style={{ backgroundImage: `url(${c.image_url})`, backgroundSize: "cover", backgroundPosition: "top center" }} />
      ) : (
        <div className="aspect-video bg-gradient-to-br from-[hsl(var(--aito-lagoon))]/20 to-[hsl(var(--aito-gold))]/20 flex items-center justify-center">
          <Shirt className="h-12 w-12 text-muted-foreground" />
        </div>
      )}
      <div className="p-4">
        {c.athlete && (
          <Link to={`/athletes/${c.athlete.slug || c.athlete.id}`} className="text-[11px] uppercase tracking-wide text-muted-foreground hover:text-[hsl(var(--aito-lagoon))]">
            AITO x {c.athlete.full_name}
          </Link>
        )}
        <h3 className="font-display font-bold text-lg leading-tight mt-1 line-clamp-2">{c.title}</h3>
        {c.objective && <p className="text-xs text-muted-foreground mt-1 line-clamp-2"><Trophy className="inline h-3 w-3 mr-1" />{c.objective}</p>}

        <div className="mt-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="font-semibold">{confirmed} / {batch} précommandes confirmées</span>
            {c.price_display && <span className="text-muted-foreground">{c.price_display}</span>}
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div className="h-full bg-[hsl(var(--aito-lagoon))] transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          {isThresholdReached ? (
            <Badge className="bg-emerald-100 text-emerald-800" data-testid="badge-threshold"><CheckCircle2 className="h-3 w-3 mr-1" />Tranche complète</Badge>
          ) : (
            <Badge variant="outline"><ShieldCheck className="h-3 w-3 mr-1" />Précommande en cours</Badge>
          )}
          <Link to={`/soutien/campagnes/${c.slug}`} className="ml-auto">
            <Button size="sm" variant="outline" data-testid={`campagne-view-${c.slug}`}>Voir <ArrowRight className="h-3.5 w-3.5 ml-1" /></Button>
          </Link>
        </div>
      </div>
    </Card>
  );
}
